import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { getBlogPostBySlug } from '../services/api';
import { ArrowLeft } from 'lucide-react';
import { BlogSEO } from '../components/SEO';

export default function BlogDetailPage() {
  const { slug } = useParams();
  const [post, setPost] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPost = async () => {
      try {
        const response = await getBlogPostBySlug(slug);
        setPost(response.data);
      } catch (error) {
        console.error('Error fetching blog post:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchPost();
  }, [slug]);

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-orange-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-600">Yüklüyor...</p>
        </div>
      </div>
    );
  }

  if (!post) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <h2 className="text-2xl font-bold text-gray-900 mb-4">Yazı bulunamadı</h2>
          <Link to="/istanbul-hikayeleri">
            <button className="px-6 py-3 bg-orange-500 text-white rounded-full hover:bg-orange-600 transition-colors">
              Hikayelere Dön
            </button>
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen">
      <BlogSEO post={post} />
      
      {/* Back Button */}
      <div className="max-w-4xl mx-auto px-6 md:px-12 py-8">
        <Link
          to="/istanbul-hikayeleri"
          className="inline-flex items-center space-x-2 text-gray-600 hover:text-orange-500 transition-colors"
          data-testid="back-to-blog"
        >
          <ArrowLeft size={20} />
          <span>Hikayelere Dön</span>
        </Link>
      </div>

      {/* Hero Image */}
      <section className="max-w-4xl mx-auto px-6 md:px-12 pb-8">
        <div className="relative h-96 md:h-[500px] rounded-2xl overflow-hidden">
                    <img 
            src={post.image_url || 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800'} 
            alt={post.title} 
            className="w-full h-full object-cover"
            onError={(e) => {
              e.target.onerror = null;
              e.target.src = 'https://images.pexels.com/photos/3264723/pexels-photo-3264723.jpeg?w=800';
            }}
          />
        </div>
      </section>

      {/* Content */}
      <article className="max-w-4xl mx-auto px-6 md:px-12 pb-24">
        <div className="bg-white rounded-2xl p-8 md:p-12 border border-gray-100">
          <div className="mb-6">
            <div className="text-sm text-orange-500 font-ui font-bold mb-4">{post.category}</div>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-6" data-testid="blog-post-title">
              {post.title}
            </h1>
            <div className="flex items-center space-x-4 text-sm text-gray-500">
              <span>{post.author}</span>
              <span>•</span>
              <span>{new Date(post.created_at).toLocaleDateString('tr-TR', { 
                year: 'numeric', 
                month: 'long', 
                day: 'numeric' 
              })}</span>
            </div>
          </div>

          <div className="prose prose-lg max-w-none">
            <p className="text-xl text-gray-600 mb-8 leading-relaxed">{post.excerpt}</p>
            <div className="text-gray-700 leading-relaxed whitespace-pre-line">{post.content}</div>
          </div>
        </div>
      </article>
    </div>
  );
}
